import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { debounce } from "lodash";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useFormContext } from "react-hook-form";

import type { CheckoutFormValuesProps } from "@/react/components/Paywalls/Checkout/types/CheckoutFormValuesProps";

import type { EmailVerificationStatus } from "../types";
import { extractErrorMessage } from "./useEmailVerificationMutations";

const DEFAULT_DEBOUNCE_MS = 800;
const CO_SUFFIX_DEBOUNCE_MS = 1500;

const isCoSuffix = (email: string) => email.toLowerCase().endsWith(".co");

interface UseEmailValidationProps {
  onEmailChange: (email: string) => void;
  onValidationError: (error: string) => void;
  onValidationStart: () => void;
  onValidationSuccess: (status: EmailVerificationStatus) => void;
  verifyEmail: (email: string) => Promise<{ status: EmailVerificationStatus }>;
}

export const useEmailValidation = ({
  verifyEmail,
  onEmailChange,
  onValidationStart,
  onValidationSuccess,
  onValidationError,
}: UseEmailValidationProps) => {
  const { watch } = useFormContext<Pick<CheckoutFormValuesProps, "email">>();
  const formEmailValue = (watch("email") || "").trim();
  const requestGenerationRef = useRef(0);
  const isMountedRef = useRef(true);

  const callbacksRef = useRef({
    verifyEmail,
    onEmailChange,
    onValidationStart,
    onValidationSuccess,
    onValidationError,
  });

  // Toast context updates replace callbacks, but are not email changes.
  useLayoutEffect(() => {
    callbacksRef.current = {
      verifyEmail,
      onEmailChange,
      onValidationStart,
      onValidationSuccess,
      onValidationError,
    };
  }, [
    verifyEmail,
    onEmailChange,
    onValidationStart,
    onValidationSuccess,
    onValidationError,
  ]);

  const validateEmail = useCallback(async (email: string) => {
    if (!isMountedRef.current || !EMAIL_REGEX.test(email)) return;

    const requestGeneration = requestGenerationRef.current;
    callbacksRef.current.onValidationStart();
    try {
      const result = await callbacksRef.current.verifyEmail(email);
      if (
        isMountedRef.current &&
        requestGeneration === requestGenerationRef.current
      ) {
        callbacksRef.current.onValidationSuccess(result.status);
      }
    } catch (error) {
      if (
        isMountedRef.current &&
        requestGeneration === requestGenerationRef.current
      ) {
        callbacksRef.current.onValidationError(extractErrorMessage(error));
      }
    }
  }, []);

  const debouncedValidateEmail = useMemo(
    () => debounce(validateEmail, DEFAULT_DEBOUNCE_MS),
    [validateEmail],
  );
  const debouncedValidateCoEmail = useMemo(
    () => debounce(validateEmail, CO_SUFFIX_DEBOUNCE_MS),
    [validateEmail],
  );
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      requestGenerationRef.current += 1;
      // Clean up these instances, not refs that could point to replacements.
      debouncedValidateEmail.cancel();
      debouncedValidateCoEmail.cancel();
    };
  }, [debouncedValidateCoEmail, debouncedValidateEmail]);

  const handleEmailChange = useCallback(
    (email: string) => {
      requestGenerationRef.current += 1;
      callbacksRef.current.onEmailChange(email);
      debouncedValidateEmail.cancel();
      debouncedValidateCoEmail.cancel();

      if (EMAIL_REGEX.test(email)) {
        void (
          isCoSuffix(email) ? debouncedValidateCoEmail : debouncedValidateEmail
        )(email);
      }
    },
    [debouncedValidateCoEmail, debouncedValidateEmail],
  );

  useEffect(() => {
    handleEmailChange(formEmailValue);
  }, [formEmailValue, handleEmailChange]);

  return {
    handleEmailChange,
  };
};
